import { useGetAttendanceCountsQuery } from '@schooly/api';
import { useMemo } from 'react';

import { AttendanceGridTrackingPoint } from '../../../Attendance/AttendanceTable/common/types';
import {
  buildTrackingPoints,
  toSession,
} from '../../../Attendance/AttendanceTable/common/trackingPoints';

type UseAttendanceCountsPointsParams = {
  schoolId: string;
  date: string;
  groupIds: string[];
  enabled: boolean;
};

export const useAttendanceCountsPoints = ({
  schoolId,
  date,
  groupIds,
  enabled,
}: UseAttendanceCountsPointsParams) => {
  const groupIdsKey = useMemo(() => [...groupIds].sort().join(','), [groupIds]);
  const canGetCounts = enabled && !!schoolId && groupIdsKey.length > 0;

  const { data, isLoading } = useGetAttendanceCountsQuery(
    {
      schoolId,
      date_from: date,
      date_to: date,
      group_ids: groupIdsKey,
    },
    { enabled: canGetCounts, refetchOnMount: 'always' },
  );

  const pointsByGroupId = useMemo(() => {
    const byGroup = new Map<string, AttendanceGridTrackingPoint[]>();
    if (!canGetCounts) return byGroup;

    for (const day of data?.results ?? []) {
      if (day.register_date !== date) continue;

      byGroup.set(day.group_id, day.tracking_points.map(countsPointToTrackingPoint));
    }

    return byGroup;
  }, [canGetCounts, data, date]);

  return {
    pointsByGroupId,
    isLoading: canGetCounts && isLoading,
  };
};

type CountsDetail = { attendance_code_id: string | null; count: number };

type CountsTrackingPoint = {
  session: string | null;
  period: { id: string; name: string; order: number } | null;
  attendance_details: CountsDetail[];
};

const countMarks = (details: CountsDetail[]) => {
  let marked = 0;
  let unmarked = 0;

  for (const detail of details) {
    if (detail.attendance_code_id === null) unmarked += detail.count;
    else marked += detail.count;
  }

  return { marked, unmarked };
};

const countsPointToTrackingPoint = (point: CountsTrackingPoint): AttendanceGridTrackingPoint => {
  const { marked, unmarked } = countMarks(point.attendance_details);

  const [trackingPoint] = buildTrackingPoints([
    {
      session: toSession(point.session),
      period: point.period
        ? {
            lesson: {
              id: point.period.id,
              name: point.period.name,
              order: point.period.order,
            },
          }
        : null,
      total_count: marked + unmarked,
      tracked_count: marked,
    },
  ]);

  return trackingPoint;
};
